import axios from 'axios';

const REQUEST_TIMEOUT_MS = 12000;
const DEFAULT_STALE_MULTIPLIER = 12;
const DEFAULT_COOLDOWN_MS = 45000;
const RETRY_DELAYS_MS = [900];
const FEATURED_COIN_COUNT = 4;
const MARKET_WATCH_COUNT = 12;
const SEARCH_RESULT_LIMIT = 8;
const COINPAPRIKA_TICKER_LIMIT = 2000;
const COINPAPRIKA_LOGO_BASE_URL = 'https://static.coinpaprika.com/coin';

const PROVIDERS = {
  coinpaprika: {
    key: 'coinpaprika',
    label: 'CoinPaprika',
    baseURL:
      process.env.REACT_APP_COINPAPRIKA_BASE_URL || 'https://api.coinpaprika.com/v1',
  },
  coingecko: {
    key: 'coingecko',
    label: 'CoinGecko',
    baseURL:
      process.env.REACT_APP_COINGECKO_BASE_URL || 'https://api.coingecko.com/api/v3',
  },
};

const configuredProviderKey = (process.env.REACT_APP_CRYPTO_PROVIDER || 'coinpaprika')
  .trim()
  .toLowerCase();
const activeProvider = PROVIDERS[configuredProviderKey] || PROVIDERS.coinpaprika;
const CACHE_PREFIX = `coinz::${activeProvider.key}::`;

const client = axios.create({
  baseURL: activeProvider.baseURL,
  timeout: REQUEST_TIMEOUT_MS,
  headers: {
    accept: 'application/json',
  },
});

const memoryCache = new Map();
const inflightRequests = new Map();
let cooldownUntil = 0;

function getStorage() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
}

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function serializeParams(params = {}) {
  return Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => `${key}=${String(value)}`)
    .join('&');
}

function buildCacheKey(path, params) {
  const query = serializeParams(params);
  return `${CACHE_PREFIX}${path}${query ? `?${query}` : ''}`;
}

function writeCacheRecord(cacheKey, record, persist = true) {
  memoryCache.set(cacheKey, record);

  if (!persist) {
    return;
  }

  const storage = getStorage();
  if (!storage) {
    return;
  }

  try {
    storage.setItem(cacheKey, JSON.stringify(record));
  } catch (error) {
    // Ignore storage write failures and keep the in-memory cache alive.
  }
}

function removeCacheRecord(cacheKey) {
  memoryCache.delete(cacheKey);

  const storage = getStorage();
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(cacheKey);
  } catch (error) {
    // Ignore storage cleanup failures.
  }
}

function readCacheRecord(cacheKey) {
  const now = Date.now();
  const cachedRecord = memoryCache.get(cacheKey);

  if (cachedRecord) {
    if (cachedRecord.staleUntil > now) {
      return cachedRecord;
    }

    removeCacheRecord(cacheKey);
    return null;
  }

  const storage = getStorage();
  if (!storage) {
    return null;
  }

  try {
    const rawRecord = storage.getItem(cacheKey);
    if (!rawRecord) {
      return null;
    }

    const parsedRecord = JSON.parse(rawRecord);
    if (parsedRecord.staleUntil <= now) {
      removeCacheRecord(cacheKey);
      return null;
    }

    memoryCache.set(cacheKey, parsedRecord);
    return parsedRecord;
  } catch (error) {
    removeCacheRecord(cacheKey);
    return null;
  }
}

function buildCacheRecord(data, ttl, staleTtl) {
  const now = Date.now();
  return {
    data,
    cachedAt: now,
    expiresAt: now + ttl,
    staleUntil: now + staleTtl,
  };
}

function buildResponse(data, meta = {}) {
  return {
    data,
    meta: {
      stale: false,
      source: 'network',
      cachedAt: null,
      provider: activeProvider.key,
      providerLabel: activeProvider.label,
      ...meta,
    },
  };
}

function buildCachedResponse(record, meta = {}) {
  return buildResponse(record.data, {
    source: 'cache',
    cachedAt: record.cachedAt,
    ...meta,
  });
}

function isRateLimitError(error) {
  return error?.response?.status === 429;
}

function isRetryableError(error) {
  const status = error?.response?.status;

  if (!status) {
    return true;
  }

  return status >= 500 || status === 429;
}

function getCooldownMs(error) {
  const retryAfterHeader = error?.response?.headers?.['retry-after'];
  const retryAfterSeconds = Number.parseInt(retryAfterHeader, 10);

  if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
    return retryAfterSeconds * 1000;
  }

  return DEFAULT_COOLDOWN_MS;
}

function toPublicError(error) {
  if (isRateLimitError(error)) {
    const rateLimitError = new Error(
      `${activeProvider.label} is rate limiting requests right now. The app will keep using cached data whenever it can.`
    );
    rateLimitError.status = 429;
    return rateLimitError;
  }

  if (error?.code === 'ECONNABORTED') {
    return new Error(`${activeProvider.label} took too long to respond. Please try again in a moment.`);
  }

  if (error?.response?.status >= 500) {
    return new Error(`${activeProvider.label} is temporarily unavailable. Please try again in a moment.`);
  }

  return new Error('Unable to load market data right now.');
}

async function requestMarketApi(path, options = {}) {
  const {
    params = {},
    ttl = 60000,
    staleTtl = ttl * DEFAULT_STALE_MULTIPLIER,
    signal,
    persist = true,
  } = options;

  const cacheKey = buildCacheKey(path, params);
  const now = Date.now();
  const cacheRecord = readCacheRecord(cacheKey);

  if (cacheRecord && cacheRecord.expiresAt > now) {
    return buildCachedResponse(cacheRecord, { stale: false });
  }

  if (cooldownUntil > now && cacheRecord) {
    return buildCachedResponse(cacheRecord, {
      stale: true,
      reason: 'cooldown',
    });
  }

  if (inflightRequests.has(cacheKey)) {
    return inflightRequests.get(cacheKey);
  }

  const requestPromise = (async () => {
    for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
      try {
        const response = await client.get(path, { params, signal });
        const record = buildCacheRecord(response.data, ttl, staleTtl);
        writeCacheRecord(cacheKey, record, persist);
        return buildResponse(response.data);
      } catch (error) {
        if (axios.isCancel(error)) {
          throw error;
        }

        if (isRateLimitError(error)) {
          cooldownUntil = Date.now() + getCooldownMs(error);
        }

        if (cacheRecord) {
          return buildCachedResponse(cacheRecord, {
            stale: true,
            reason: isRateLimitError(error) ? 'rate-limit' : 'fallback',
          });
        }

        const shouldRetry = attempt < RETRY_DELAYS_MS.length && isRetryableError(error);
        if (shouldRetry) {
          await sleep(RETRY_DELAYS_MS[attempt]);
          continue;
        }

        throw toPublicError(error);
      }
    }

    throw new Error('Unable to load market data right now.');
  })();

  inflightRequests.set(cacheKey, requestPromise);

  try {
    return await requestPromise;
  } finally {
    inflightRequests.delete(cacheKey);
  }
}

function mergeMeta(...results) {
  const metas = results.map((result) => result?.meta).filter(Boolean);

  if (!metas.length) {
    return {
      stale: false,
      source: 'network',
      cachedAt: null,
      provider: activeProvider.key,
      providerLabel: activeProvider.label,
    };
  }

  return {
    stale: metas.some((meta) => meta.stale),
    source: metas.every((meta) => meta.source === 'cache')
      ? 'cache'
      : metas.some((meta) => meta.source === 'cache')
        ? 'mixed'
        : 'network',
    cachedAt: metas.find((meta) => meta.cachedAt)?.cachedAt || null,
    provider: metas[0].provider || activeProvider.key,
    providerLabel: metas[0].providerLabel || activeProvider.label,
  };
}

function mapMarketCoin(coin = {}, fallback = {}) {
  return {
    id: coin.id || fallback.id,
    name: coin.name || fallback.name,
    symbol: (coin.symbol || fallback.symbol || '').toUpperCase(),
    image: coin.image || fallback.image || fallback.large || fallback.thumb,
    currentPrice:
      coin.current_price ??
      fallback.currentPrice ??
      fallback.current_price ??
      null,
    priceChange24h:
      coin.price_change_percentage_24h_in_currency ??
      coin.price_change_percentage_24h ??
      fallback.priceChange24h ??
      null,
    marketCapRank: coin.market_cap_rank ?? fallback.marketCapRank ?? null,
    marketCap: coin.market_cap ?? fallback.marketCap ?? null,
    totalVolume: coin.total_volume ?? fallback.totalVolume ?? null,
  };
}

async function fetchMarketsByIds(ids, options = {}) {
  if (!ids.length) {
    return buildResponse([]);
  }

  const response = await requestMarketApi('/coins/markets', {
    ttl: 90000,
    staleTtl: 20 * 60 * 1000,
    ...options,
    params: {
      vs_currency: 'usd',
      ids: ids.join(','),
      sparkline: false,
      order: 'market_cap_desc',
      price_change_percentage: '24h',
      per_page: ids.length,
      page: 1,
      locale: 'en',
      ...(options.params || {}),
    },
  });

  return {
    data: response.data.map((coin) => mapMarketCoin(coin)),
    meta: response.meta,
  };
}

async function fetchCoinGeckoFeaturedCoins() {
  const response = await requestMarketApi('/coins/markets', {
    ttl: 90000,
    staleTtl: 20 * 60 * 1000,
    params: {
      vs_currency: 'usd',
      order: 'market_cap_desc',
      per_page: FEATURED_COIN_COUNT,
      page: 1,
      sparkline: false,
      price_change_percentage: '24h',
      locale: 'en',
    },
  });

  return {
    data: response.data.map((coin) => mapMarketCoin(coin)),
    meta: response.meta,
  };
}

async function fetchCoinGeckoTrendingCoins() {
  const trendingResponse = await requestMarketApi('/search/trending', {
    ttl: 5 * 60 * 1000,
    staleTtl: 30 * 60 * 1000,
  });

  const trendingCoins = trendingResponse.data.coins.map(({ item }) => ({
    id: item.id,
    name: item.name,
    symbol: item.symbol,
    image: item.large || item.thumb,
    marketCapRank: item.market_cap_rank,
  }));

  const marketResponse = await fetchMarketsByIds(
    trendingCoins.map((coin) => coin.id)
  );
  const marketMap = new Map(marketResponse.data.map((coin) => [coin.id, coin]));

  return {
    data: trendingCoins.map((coin) => mapMarketCoin(marketMap.get(coin.id), coin)),
    meta: mergeMeta(trendingResponse, marketResponse),
  };
}

async function searchCoinGecko(query) {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return buildResponse([]);
  }

  const searchResponse = await requestMarketApi('/search', {
    ttl: 2 * 60 * 1000,
    staleTtl: 30 * 60 * 1000,
    params: {
      query: trimmedQuery,
    },
  });

  const matches = searchResponse.data.coins.slice(0, SEARCH_RESULT_LIMIT).map((coin) => ({
    id: coin.id,
    name: coin.name,
    symbol: coin.symbol,
    image: coin.large || coin.thumb,
    marketCapRank: coin.market_cap_rank,
  }));

  const marketResponse = await fetchMarketsByIds(matches.map((coin) => coin.id));
  const marketMap = new Map(marketResponse.data.map((coin) => [coin.id, coin]));

  return {
    data: matches.map((coin) => mapMarketCoin(marketMap.get(coin.id), coin)),
    meta: mergeMeta(searchResponse, marketResponse),
  };
}

async function fetchCoinGeckoDetails(id) {
  const [coinResponse, chartResponse] = await Promise.all([
    requestMarketApi(`/coins/${id}`, {
      ttl: 2 * 60 * 1000,
      staleTtl: 30 * 60 * 1000,
      params: {
        localization: false,
        tickers: false,
        community_data: false,
        developer_data: false,
        sparkline: false,
      },
    }),
    requestMarketApi(`/coins/${id}/market_chart`, {
      ttl: 2 * 60 * 1000,
      staleTtl: 30 * 60 * 1000,
      params: {
        vs_currency: 'usd',
        days: 30,
        interval: 'daily',
      },
    }),
  ]);

  return {
    data: {
      coin: coinResponse.data,
      chart: chartResponse.data,
    },
    meta: mergeMeta(coinResponse, chartResponse),
  };
}

function buildCoinPaprikaLogoUrl(id) {
  return `${COINPAPRIKA_LOGO_BASE_URL}/${id}/logo.png`;
}

function mapCoinPaprikaMarketCoin(coin = {}) {
  const usdQuote = coin.quotes?.USD || coin.quotes?.usd || {};

  return {
    id: coin.id,
    name: coin.name,
    symbol: (coin.symbol || '').toUpperCase(),
    image: buildCoinPaprikaLogoUrl(coin.id),
    currentPrice: usdQuote.price ?? null,
    priceChange24h: usdQuote.percent_change_24h ?? null,
    marketCapRank: coin.rank ?? null,
    marketCap: usdQuote.market_cap ?? null,
    totalVolume: usdQuote.volume_24h ?? null,
  };
}

function sortCoinPaprikaTickers(left, right) {
  const leftRank = left.rank || Number.MAX_SAFE_INTEGER;
  const rightRank = right.rank || Number.MAX_SAFE_INTEGER;
  return leftRank - rightRank;
}

async function fetchCoinPaprikaTickers() {
  const response = await requestMarketApi('/tickers', {
    ttl: 5 * 60 * 1000,
    staleTtl: 60 * 60 * 1000,
    persist: false,
    params: {
      quotes: 'USD',
    },
  });

  const tickers = response.data
    .filter((coin) => coin.is_active !== false)
    .sort(sortCoinPaprikaTickers)
    .slice(0, COINPAPRIKA_TICKER_LIMIT);

  return {
    data: tickers,
    meta: response.meta,
  };
}

function searchCoinPaprikaTickers(tickers, query) {
  const normalizedQuery = query.trim().toLowerCase();

  return tickers
    .filter((coin) => {
      const name = (coin.name || '').toLowerCase();
      const symbol = (coin.symbol || '').toLowerCase();
      return name.includes(normalizedQuery) || symbol.includes(normalizedQuery);
    })
    .sort((left, right) => {
      const leftName = (left.name || '').toLowerCase();
      const rightName = (right.name || '').toLowerCase();
      const leftSymbol = (left.symbol || '').toLowerCase();
      const rightSymbol = (right.symbol || '').toLowerCase();
      const leftExact = leftName === normalizedQuery || leftSymbol === normalizedQuery;
      const rightExact = rightName === normalizedQuery || rightSymbol === normalizedQuery;

      if (leftExact !== rightExact) {
        return leftExact ? -1 : 1;
      }

      const leftStartsWith =
        leftName.startsWith(normalizedQuery) || leftSymbol.startsWith(normalizedQuery);
      const rightStartsWith =
        rightName.startsWith(normalizedQuery) || rightSymbol.startsWith(normalizedQuery);

      if (leftStartsWith !== rightStartsWith) {
        return leftStartsWith ? -1 : 1;
      }

      return sortCoinPaprikaTickers(left, right);
    })
    .slice(0, SEARCH_RESULT_LIMIT);
}

async function fetchCoinPaprikaTicker(id) {
  const response = await requestMarketApi(`/tickers/${id}`, {
    ttl: 5 * 60 * 1000,
    staleTtl: 60 * 60 * 1000,
    params: {
      quotes: 'USD',
    },
  });

  return {
    data: mapCoinPaprikaMarketCoin(response.data),
    meta: response.meta,
  };
}

function formatDateOffset(daysAgo) {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString().slice(0, 10);
}

function extractTwitterHandle(links = []) {
  const twitterLink = links.find((link) => link.type === 'twitter' && link.url);
  if (!twitterLink?.url) {
    return '';
  }

  try {
    const { pathname } = new URL(twitterLink.url);
    return pathname.replace(/^\/+/, '').split('/')[0] || '';
  } catch (error) {
    return twitterLink.url
      .replace(/^https?:\/\/(www\.)?(twitter|x)\.com\//i, '')
      .split(/[/?#]/)[0];
  }
}

function mapCoinPaprikaDetails(coin, ticker) {
  const usdQuote = ticker?.quotes?.USD || ticker?.quotes?.usd || {};

  return {
    id: coin.id,
    name: coin.name,
    symbol: coin.symbol,
    image: {
      small: coin.logo || buildCoinPaprikaLogoUrl(coin.id),
      large: coin.logo || buildCoinPaprikaLogoUrl(coin.id),
    },
    description: {
      en: coin.description || '',
    },
    links: {
      homepage: coin.links?.website || [],
      twitter_screen_name: extractTwitterHandle(coin.links_extended || []),
      blockchain_site: coin.links?.explorer || [],
    },
    market_data: {
      current_price: {
        usd: usdQuote.price ?? null,
      },
      price_change_percentage_24h: usdQuote.percent_change_24h ?? null,
      market_cap: {
        usd: usdQuote.market_cap ?? null,
      },
      market_cap_rank: ticker?.rank ?? coin.rank ?? null,
      high_24h: {
        usd: null,
      },
      low_24h: {
        usd: null,
      },
      circulating_supply: ticker?.circulating_supply ?? null,
      total_supply: ticker?.total_supply ?? null,
      price_change_percentage_1y: usdQuote.percent_change_1y ?? null,
    },
  };
}

async function fetchCoinPaprikaFeaturedCoins() {
  const response = await fetchCoinPaprikaTickers();

  return {
    data: response.data.slice(0, FEATURED_COIN_COUNT).map(mapCoinPaprikaMarketCoin),
    meta: response.meta,
  };
}

async function fetchCoinPaprikaTrendingCoins() {
  const response = await fetchCoinPaprikaTickers();

  return {
    data: response.data.slice(0, MARKET_WATCH_COUNT).map(mapCoinPaprikaMarketCoin),
    meta: response.meta,
  };
}

async function searchCoinPaprika(query) {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return buildResponse([]);
  }

  const tickersResponse = await fetchCoinPaprikaTickers();
  const localMatches = searchCoinPaprikaTickers(tickersResponse.data, trimmedQuery);

  if (localMatches.length >= SEARCH_RESULT_LIMIT) {
    return {
      data: localMatches.map(mapCoinPaprikaMarketCoin),
      meta: tickersResponse.meta,
    };
  }

  const searchResponse = await requestMarketApi('/search', {
    ttl: 10 * 60 * 1000,
    staleTtl: 60 * 60 * 1000,
    params: {
      q: trimmedQuery,
      c: 'currencies',
      limit: SEARCH_RESULT_LIMIT * 2,
    },
  });

  const seenIds = new Set(localMatches.map((coin) => coin.id));
  const fallbackIds = (searchResponse.data.currencies || [])
    .filter((coin) => coin.is_active !== false)
    .map((coin) => coin.id)
    .filter((id) => !seenIds.has(id))
    .slice(0, SEARCH_RESULT_LIMIT - localMatches.length);

  const fallbackResults = await Promise.allSettled(
    fallbackIds.map((id) => fetchCoinPaprikaTicker(id))
  );
  const fulfilledFallbacks = fallbackResults
    .filter((result) => result.status === 'fulfilled')
    .map((result) => result.value);

  return {
    data: [
      ...localMatches.map(mapCoinPaprikaMarketCoin),
      ...fulfilledFallbacks.map((result) => result.data),
    ].slice(0, SEARCH_RESULT_LIMIT),
    meta: mergeMeta(tickersResponse, searchResponse, ...fulfilledFallbacks),
  };
}

async function fetchCoinPaprikaDetails(id) {
  const [coinResponse, tickerResponse, chartResponse] = await Promise.all([
    requestMarketApi(`/coins/${id}`, {
      ttl: 2 * 60 * 1000,
      staleTtl: 30 * 60 * 1000,
    }),
    requestMarketApi(`/tickers/${id}`, {
      ttl: 2 * 60 * 1000,
      staleTtl: 30 * 60 * 1000,
      params: {
        quotes: 'USD',
      },
    }),
    requestMarketApi(`/tickers/${id}/historical`, {
      ttl: 10 * 60 * 1000,
      staleTtl: 60 * 60 * 1000,
      params: {
        start: formatDateOffset(30),
        interval: '1d',
        quote: 'usd',
      },
    }),
  ]);

  return {
    data: {
      coin: mapCoinPaprikaDetails(coinResponse.data, tickerResponse.data),
      chart: {
        prices: chartResponse.data.map((point) => [point.timestamp, point.price]),
      },
    },
    meta: mergeMeta(coinResponse, tickerResponse, chartResponse),
  };
}

const providerHandlers = {
  coinpaprika: {
    fetchFeaturedCoins: fetchCoinPaprikaFeaturedCoins,
    fetchTrendingCoins: fetchCoinPaprikaTrendingCoins,
    searchCoins: searchCoinPaprika,
    fetchCoinDetails: fetchCoinPaprikaDetails,
  },
  coingecko: {
    fetchFeaturedCoins: fetchCoinGeckoFeaturedCoins,
    fetchTrendingCoins: fetchCoinGeckoTrendingCoins,
    searchCoins: searchCoinGecko,
    fetchCoinDetails: fetchCoinGeckoDetails,
  },
};

const activeHandler = providerHandlers[activeProvider.key] || providerHandlers.coinpaprika;

export async function fetchFeaturedCoins() {
  return activeHandler.fetchFeaturedCoins();
}

export async function fetchTrendingCoins() {
  return activeHandler.fetchTrendingCoins();
}

export async function searchCoins(query) {
  return activeHandler.searchCoins(query);
}

export async function fetchCoinDetails(id) {
  return activeHandler.fetchCoinDetails(id);
}

export function getMarketDataProviderName() {
  return activeProvider.label;
}

export function getStabilityNotice(meta) {
  if (!meta?.stale) {
    return '';
  }

  return `Showing cached data while ${meta.providerLabel || activeProvider.label} is rate limiting requests or temporarily unavailable.`;
}
